/**
 * /feeds — upgraded list + metrics + filters + row actions.
 *
 * Row click navigates to /feeds/$slug (see routes/feedDetail.tsx).
 * Replay / pause / resume / delete are per-row and mutate via the wizard hooks.
 */

import { useMemo, useState } from "react";
import {
    Activity,
    AlertTriangle,
    Pause,
    Play,
    Rss,
    Search,
    ShieldX,
    Trash2,
    Zap,
} from "lucide-react";
import { Link, Navigate, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { useCurrentUser } from "~/lib/auth";
import {
    type FeedSourceRecord,
    useConnectors,
    useDeleteFeedSource,
    useFeedSources,
    useFormats,
    usePauseFeed,
    useReplayFeed,
    useResumeFeed,
} from "~/lib/feeds";
import { CreateFeedDialog } from "~/components/CreateFeedDialog";
import { TableSkeleton } from "~/components/TableSkeleton";

// ---------- derivations ----------

interface DerivedStatus {
    pillClass: string;
    label: string;
}

// FEED_CATEGORIES is the canonical category list shown in the Categories
// dropdown. Order is the rendering order. Each entry is a substring matched
// against the feed's slug — first match wins. "other" is the catch-all.
const FEED_CATEGORIES = [
    "spam",          // uceprotect, spamhaus, dnsbl-style
    "phishing",      // phishunt, phishing_database, openphish
    "malware",       // malwarebazaar, malpedia
    "c2",            // urlhaus, threatfox, feodotracker
    "vulnerability", // cisa_kev, dshield_nrd
    "osint",         // otx_pulses, circl_osint, infinitumit
    "scanner",       // urlscan, censys
] as const;
type FeedCategory = (typeof FEED_CATEGORIES)[number] | "other";

function deriveCategory(f: FeedSourceRecord): FeedCategory {
    const slug = f.slug.toLowerCase();
    if (slug.includes("uceprotect") || slug.includes("spamhaus")) return "spam";
    if (slug.includes("phish") || slug.includes("openphish")) return "phishing";
    if (slug.includes("malwarebazaar") || slug.includes("malpedia")) return "malware";
    if (slug.includes("urlhaus") || slug.includes("threatfox") || slug.includes("feodo")) return "c2";
    if (slug.includes("cisa") || slug.includes("kev") || slug.includes("dshield")) return "vulnerability";
    if (slug.includes("otx") || slug.includes("circl") || slug.includes("infinitumit")) return "osint";
    if (slug.includes("urlscan") || slug.includes("censys")) return "scanner";
    return "other";
}

function deriveStatus(f: FeedSourceRecord): DerivedStatus {
    if (!f.enabled && f.lastStatus === "paused") {
        return { pillClass: "status-pill--paused", label: "paused" };
    }
    if (!f.enabled) {
        return { pillClass: "status-pill--draft", label: "disabled" };
    }
    if (f.lastStatus === "error") return { pillClass: "status-pill--failed", label: "error" };
    if (f.lastStatus === "ok") return { pillClass: "status-pill--live", label: "active" };
    return { pillClass: "status-pill--queued", label: f.lastStatus ?? "pending" };
}

function formatRelative(iso?: string): string {
    if (!iso) return "never";
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return iso;
    const diff = Date.now() - ts;
    if (diff < 0) return "scheduled";
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
}

// ---------- page ----------

export function FeedsPage(): React.ReactElement {
    const me = useCurrentUser();
    const allowed = me.data?.roles.some((r) => r === "admin" || r === "operator");
    const feeds = useFeedSources();
    const connectors = useConnectors();
    const formats = useFormats();
    const navigate = useNavigate();

    const [search, setSearch] = useState("");
    const [connectorFilter, setConnectorFilter] = useState("");
    const [formatFilter, setFormatFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [healthyOnly, setHealthyOnly] = useState(false);

    const filtered = useMemo(() => {
        const rows = feeds.data ?? [];
        const q = search.trim().toLowerCase();
        return rows.filter((f) => {
            if (q && !(f.name.toLowerCase().includes(q) || f.slug.toLowerCase().includes(q))) return false;
            // Legacy record doesn't carry connector/format kinds; the single
            // `kind` field is the connector legacy alias. We keep the filter
            // wired for future-proofing.
            if (connectorFilter && f.kind !== connectorFilter) return false;
            if (formatFilter && f.kind !== formatFilter) return false;
            if (categoryFilter && deriveCategory(f) !== categoryFilter) return false;
            const status = deriveStatus(f).label;
            if (statusFilter && status !== statusFilter) return false;
            if (healthyOnly && status !== "active") return false;
            return true;
        });
    }, [feeds.data, search, connectorFilter, formatFilter, statusFilter, categoryFilter, healthyOnly]);

    if (me.data && !allowed) {
        return <Navigate to="/" />;
    }

    const rows = feeds.data ?? [];
    const activeCount = rows.filter((f) => deriveStatus(f).label === "active").length;
    const pausedCount = rows.filter((f) => deriveStatus(f).label === "paused").length;
    const errorCount  = rows.filter((f) => deriveStatus(f).label === "error").length;
    const ingestedLast24h = rows.reduce((n, f) => n + (f.lastIngestedCount ?? 0), 0);

    return (
        <div className="stack-lg">
            <header className="page-header">
                <div className="page-header__top">
                    <div className="page-header__heading">
                        <span className="page-header__eyebrow">
                            <span className="page-header__eyebrow__icon"><Rss size={14} aria-hidden /></span>
                            Data intake
                        </span>
                        <h1 className="page-header__title">Feeds</h1>
                        <p className="page-header__description">
                            Connectors, formats, mappings. No backend deploy required: pick a
                            shape, promote the draft, done.
                        </p>
                    </div>
                    <div className="page-header__actions">
                        <CreateFeedDialog
                            onCreated={(slug) => {
                                void navigate({ to: "/feeds/$slug", params: { slug } });
                            }}
                        />
                    </div>
                </div>
                <div className="page-header__meta">
                    <span className="page-header__meta__item tabular-nums">
                        {rows.length.toLocaleString()} sources
                    </span>
                    <span className="page-header__meta__item tabular-nums">
                        {activeCount} active
                    </span>
                    {errorCount > 0 && (
                        <span className="page-header__meta__item tabular-nums text-danger">
                            {errorCount} failing
                        </span>
                    )}
                    <span className="page-header__meta__item tabular-nums" title="Sum of new IoCs added on each feed's most recent run (after dedup). Not the total fetched volume.">
                        {ingestedLast24h.toLocaleString()} new iocs last run
                    </span>
                </div>
            </header>

            <div className="metric-grid">
                <MetricCard label="Total feeds" value={rows.length} />
                <MetricCard label="Active" value={activeCount} tone="success" />
                <MetricCard label="Paused" value={pausedCount} tone="accent" />
                <MetricCard label="Failing" value={errorCount} tone="danger" />
                <MetricCard label="Last-run IoCs" value={ingestedLast24h} />
            </div>

            <section className="section-card" aria-label="Feeds">
                <div className="section-card__header">
                    <div className="section-card__title-group">
                        <h2 className="section-card__title">Sources</h2>
                        <p className="section-card__subtitle">
                            Click a row to open the mapping editor, samples, and run history.
                        </p>
                    </div>
                </div>
                <div className="toolbar">
                    <div className="toolbar__search">
                        <Search size={14} className="toolbar__search__icon" aria-hidden />
                        <input
                            className="input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name or slug"
                            aria-label="Search feeds"
                        />
                    </div>
                    <label className="toolbar__filter">
                        <span className="toolbar__filter__label">Connector</span>
                        <select
                            className="input"
                            value={connectorFilter}
                            onChange={(e) => setConnectorFilter(e.target.value)}
                        >
                            <option value="">All</option>
                            {(connectors.data ?? []).map((c) => (
                                <option key={c.kind} value={c.kind}>{c.displayName}</option>
                            ))}
                        </select>
                    </label>
                    <label className="toolbar__filter">
                        <span className="toolbar__filter__label">Format</span>
                        <select
                            className="input"
                            value={formatFilter}
                            onChange={(e) => setFormatFilter(e.target.value)}
                        >
                            <option value="">All</option>
                            {(formats.data ?? []).map((f) => (
                                <option key={f.kind} value={f.kind}>{f.displayName}</option>
                            ))}
                        </select>
                    </label>
                    <label className="toolbar__filter">
                        <span className="toolbar__filter__label">Category</span>
                        <select
                            className="input"
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                        >
                            <option value="">All</option>
                            {FEED_CATEGORIES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                            <option value="other">other</option>
                        </select>
                    </label>
                    <label className="toolbar__filter">
                        <span className="toolbar__filter__label">Status</span>
                        <select
                            className="input"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">All</option>
                            <option value="active">Active</option>
                            <option value="paused">Paused</option>
                            <option value="disabled">Disabled</option>
                            <option value="error">Error</option>
                            <option value="pending">Pending</option>
                        </select>
                    </label>
                    <label className="cluster-xs text-12">
                        <input
                            type="checkbox"
                            checked={healthyOnly}
                            onChange={(e) => setHealthyOnly(e.target.checked)}
                            aria-pressed={healthyOnly}
                        />
                        Healthy only
                    </label>
                </div>

                {feeds.isLoading && <TableSkeleton rows={8} columns={7} />}
                {feeds.isError && (
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Could not load feeds</div>
                        <div className="empty-state__body">Try refreshing in a moment.</div>
                    </div>
                )}
                {feeds.data && filtered.length === 0 && (
                    <div className="empty-state">
                        <Rss size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No feeds match your filters</div>
                        <div className="empty-state__body">
                            Adjust the search or tap “New feed” to create one.
                        </div>
                    </div>
                )}

                {feeds.data && filtered.length > 0 && (
                    <div style={{ overflowX: "auto" }}>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Kind</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Status</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Last run</th>
                                    <th style={{ textAlign: "right", width: 1, whiteSpace: "nowrap" }} title="Rows fetched from upstream on the most recent run (raw volume, before dedup).">Fetched</th>
                                    <th style={{ textAlign: "right", width: 1, whiteSpace: "nowrap" }} title="New IoCs added on the most recent run, after dedup against the existing catalog.">New IoCs</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap" }}>Breaker</th>
                                    <th style={{ width: 1, whiteSpace: "nowrap", textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((f) => <FeedRow key={f.slug} feed={f} />)}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}

// ---------- subcomponents ----------

function MetricCard({
    label,
    value,
    tone,
}: {
    label: string;
    value: number;
    tone?: "accent" | "danger" | "success";
}): React.ReactElement {
    const cls = tone ? `metric-card metric-card--${tone}` : "metric-card";
    const icon =
        tone === "danger" ? <AlertTriangle size={12} /> :
        tone === "success" ? <Activity size={12} /> :
        tone === "accent" ? <Pause size={12} /> :
        <Rss size={12} />;
    return (
        <div className={cls}>
            <span className="metric-card__label">
                <span className="metric-card__label__icon">{icon}</span>
                {label}
            </span>
            <span className="metric-card__value tabular-nums">{value.toLocaleString()}</span>
        </div>
    );
}

function FeedRow({ feed }: { feed: FeedSourceRecord }): React.ReactElement {
    const navigate = useNavigate();
    const st = deriveStatus(feed);
    const replay = useReplayFeed(feed.slug);
    const pause = usePauseFeed(feed.slug);
    const resume = useResumeFeed(feed.slug);
    const del = useDeleteFeedSource();

    function goDetail(): void {
        void navigate({ to: "/feeds/$slug", params: { slug: feed.slug } });
    }

    async function onReplay(e: React.MouseEvent): Promise<void> {
        e.stopPropagation();
        try {
            await replay.mutateAsync();
            toast.success(`Replay queued for ${feed.slug}`);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Replay failed");
        }
    }
    async function onPauseResume(e: React.MouseEvent): Promise<void> {
        e.stopPropagation();
        try {
            if (feed.enabled) {
                await pause.mutateAsync();
                toast.success(`${feed.slug} paused`);
            } else {
                await resume.mutateAsync();
                toast.success(`${feed.slug} resumed`);
            }
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Operation failed");
        }
    }
    async function onDelete(e: React.MouseEvent): Promise<void> {
        e.stopPropagation();
        if (!confirm(`Delete feed ${feed.slug}? If it has sightings it will be retired instead.`)) return;
        try {
            await del.mutateAsync(feed.slug);
            toast.success(`${feed.slug} removed`);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Delete failed");
        }
    }

    return (
        <tr
            onClick={goDetail}
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter") goDetail();
            }}
            style={{ cursor: "pointer" }}
        >
            <td style={{ maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <div className="stack-xs">
                    <Link
                        to="/feeds/$slug"
                        params={{ slug: feed.slug }}
                        className="text-13"
                        style={{ color: "var(--color-text)", textDecoration: "none" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {feed.name}
                    </Link>
                    <code className="type-mono-sm text-text-muted" style={{ fontSize: "var(--font-size-11)" }}>
                        {feed.slug}
                    </code>
                </div>
            </td>
            <td style={{ whiteSpace: "nowrap" }}>
                <span className="chip">{feed.kind}</span>
            </td>
            <td style={{ whiteSpace: "nowrap" }}>
                <span className={`status-pill ${st.pillClass}`}>{st.label}</span>
            </td>
            <td className="text-12 text-text-muted" style={{ whiteSpace: "nowrap" }}>
                {formatRelative(feed.lastRunAt)}
            </td>
            <td className="text-13 tabular-nums text-text-muted" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                {(feed.lastRowsFetched ?? 0).toLocaleString()}
            </td>
            <td className="text-13 tabular-nums" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                {feed.lastIngestedCount.toLocaleString()}
            </td>
            <td className="text-12 tabular-nums text-text-muted" style={{ whiteSpace: "nowrap" }}>
                {/* Legacy list row doesn't carry circuit-breaker fields — render em-dash */}
                —
            </td>
            <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                <div className="cluster-xs" style={{ justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                    <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={onReplay}
                        disabled={replay.isPending}
                        aria-label={`Replay ${feed.slug}`}
                        title="Replay"
                    >
                        <Zap size={12} />
                    </button>
                    <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={onPauseResume}
                        disabled={pause.isPending || resume.isPending}
                        aria-pressed={!feed.enabled}
                        aria-label={feed.enabled ? `Pause ${feed.slug}` : `Resume ${feed.slug}`}
                        title={feed.enabled ? "Pause" : "Resume"}
                    >
                        {feed.enabled ? <Pause size={12} /> : <Play size={12} />}
                    </button>
                    <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={onDelete}
                        disabled={del.isPending}
                        aria-label={`Delete ${feed.slug}`}
                        title="Delete"
                    >
                        <Trash2 size={12} />
                    </button>
                </div>
            </td>
        </tr>
    );
}
